import type { PolicyStatement } from ':helpers/casl/abac/policy.types';

import { AccessLevel } from ':helpers/casl/abac/access-levels';
import { getStatementsForLevel, SubSectionName } from ':helpers/casl/abac/permission-catalogue';
import { ORG_SCOPE_PLACEHOLDER } from ':helpers/casl/abac/policy-scope';
import { CaslAction, CaslSubject } from ':helpers/casl/casl.enum';

/**
 * Predefined roles seeded per organization.
 * Only OWNER / EDITOR / MODERATOR / GUEST are predefined.
 * At migration time, the legacy ADMIN role maps to OWNER, and organizations with the legacy REDACTOR / POST_REDACTOR roles get a dedicated custom role.
 *
 * A role is a list of (sub-section, level) grants expanded through the permission catalogue.
 * For OWNER only, it also carries the organization-level (admin) statements that are not part of the front catalogue.
 */
interface RoleGrant {
    subSection: SubSectionName;
    level: AccessLevel;
}

interface PredefinedRoleDefinition {
    name: string;
    description: string;
    grants: RoleGrant[];
    extraStatements?: PolicyStatement[];
}

// Organization-level admin statements that have no place in the front catalogue. OWNER only.
// User handling now lives in the SETTINGS/Users sub-section (EDIT level), so it is no longer carried here.
const ownerOrganizationStatements: PolicyStatement[] = [
    {
        effect: 'allow',
        action: [CaslAction.CREATE],
        subject: CaslSubject.RESTAURANT,
        conditions: { organizationId: { $in: ORG_SCOPE_PLACEHOLDER } },
    },
    {
        effect: 'allow',
        action: [CaslAction.MANAGE],
        subject: CaslSubject.CREDENTIAL,
        conditions: { organization: { $in: ORG_SCOPE_PLACEHOLDER } },
    },
];

const PREDEFINED_ROLE_DEFINITIONS: PredefinedRoleDefinition[] = [
    {
        name: 'Owner',
        description: 'Full access — organization management and all restaurant permissions.',
        grants: [
            { subSection: SubSectionName.INFORMATIONS, level: AccessLevel.EDIT },
            { subSection: SubSectionName.SEO_POSTS, level: AccessLevel.EDIT },
            { subSection: SubSectionName.INSPIRATIONS, level: AccessLevel.EDIT },
            { subSection: SubSectionName.SOCIAL_POSTS, level: AccessLevel.PUBLISH },
            { subSection: SubSectionName.REVIEWS, level: AccessLevel.PUBLISH },
            { subSection: SubSectionName.CAMPAIGNS, level: AccessLevel.EDIT },
            { subSection: SubSectionName.COMMENTS, level: AccessLevel.EDIT },
            { subSection: SubSectionName.KEYWORDS, level: AccessLevel.EDIT },
            { subSection: SubSectionName.CLIENTS, level: AccessLevel.EDIT },
            { subSection: SubSectionName.PLATFORMS, level: AccessLevel.EDIT },
            { subSection: SubSectionName.USERS, level: AccessLevel.EDIT },
        ],
        extraStatements: ownerOrganizationStatements,
    },
    {
        name: 'Editor',
        description: 'Restaurant-level editor without organization management or platform connection.',
        grants: [
            { subSection: SubSectionName.INFORMATIONS, level: AccessLevel.EDIT },
            { subSection: SubSectionName.SEO_POSTS, level: AccessLevel.EDIT },
            { subSection: SubSectionName.INSPIRATIONS, level: AccessLevel.EDIT },
            { subSection: SubSectionName.SOCIAL_POSTS, level: AccessLevel.PUBLISH },
            { subSection: SubSectionName.REVIEWS, level: AccessLevel.PUBLISH },
            { subSection: SubSectionName.CAMPAIGNS, level: AccessLevel.EDIT },
            { subSection: SubSectionName.COMMENTS, level: AccessLevel.EDIT },
            { subSection: SubSectionName.KEYWORDS, level: AccessLevel.EDIT },
            { subSection: SubSectionName.CLIENTS, level: AccessLevel.EDIT },
            { subSection: SubSectionName.USERS, level: AccessLevel.READ },
        ],
    },
    {
        name: 'Moderator',
        description: 'Moderates reviews, social posts and comments; cannot publish without validation.',
        grants: [
            { subSection: SubSectionName.INFORMATIONS, level: AccessLevel.EDIT },
            { subSection: SubSectionName.SOCIAL_POSTS, level: AccessLevel.EDIT },
            { subSection: SubSectionName.REVIEWS, level: AccessLevel.VALIDATE },
            { subSection: SubSectionName.COMMENTS, level: AccessLevel.EDIT },
            { subSection: SubSectionName.USERS, level: AccessLevel.READ },
        ],
    },
    {
        name: 'Guest',
        description: 'Read-only access to the restaurant users list.',
        grants: [{ subSection: SubSectionName.USERS, level: AccessLevel.READ }],
    },
];

function expandRoleStatements(definition: PredefinedRoleDefinition): PolicyStatement[] {
    const seen = new Set<string>();
    const statements: PolicyStatement[] = [];
    const push = (statement: PolicyStatement): void => {
        const key = JSON.stringify(statement);
        if (seen.has(key)) return;
        seen.add(key);
        statements.push(statement);
    };

    for (const { subSection, level } of definition.grants) {
        for (const statement of getStatementsForLevel(subSection, level)) {
            push({ effect: 'allow', ...statement });
        }
    }
    for (const statement of definition.extraStatements ?? []) {
        push(statement);
    }
    return statements;
}

export interface PredefinedRole {
    name: string;
    description: string;
    statements: PolicyStatement[];
}

export const PREDEFINED_ROLES: PredefinedRole[] = PREDEFINED_ROLE_DEFINITIONS.map((definition) => ({
    name: definition.name,
    description: definition.description,
    statements: expandRoleStatements(definition),
}));
